import { useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPokemonById } from '../store/pokemonSlice';
import { selectIsAuthenticated } from '../store/authSlice';
import {
  selectIsFavorite, selectIsShiny,
  addFavorite, removeFavorite,
  addShiny,
} from '../store/userDataSlice';
import TypeBadge from '../components/TypeBadge';
import { POKEMON_DATA } from '../data/pokemonData';
import { LEARNSETS } from '../data/learnsets';
import './PokemonDetail.css';

// ── Static lookup tables ────────────────────────────────────────────────────

// All base-game Pokemon (IDs 1–1025) sorted by ID for prev/next navigation
const SORTED_BASE_KEYS = Object.entries(POKEMON_DATA)
  .filter(([, v]) => v.id >= 1 && v.id <= 1025)
  .sort((a, b) => a[1].id - b[1].id)
  .map(([k]) => k);

const STAT_LABELS = {
  hp: 'HP', attack: 'Atk', defense: 'Def',
  'special-attack': 'SpA', 'special-defense': 'SpD', speed: 'Spe',
};

const STAT_COLORS = {
  hp: '#FF5959', attack: '#F5AC78', defense: '#FAE078',
  'special-attack': '#9DB7F5', 'special-defense': '#A7DB8D', speed: '#FA92B2',
};

// ── Helper functions ─────────────────────────────────────────────────────────

/** Resolve URL param (key or numeric string) → { pokemonKey, numericId, useLocalData } */
function resolveParam(param) {
  // Direct key match in POKEMON_DATA (e.g. "charizard", "charizard-mega-x")
  if (POKEMON_DATA[param]) {
    const id = POKEMON_DATA[param].id;
    return { pokemonKey: param, numericId: id, useLocalData: id > 1025 };
  }
  const num = parseInt(param, 10);
  if (!isNaN(num)) {
    const entry = Object.entries(POKEMON_DATA).find(([, v]) => v.id === num);
    if (entry) {
      return { pokemonKey: entry[0], numericId: num, useLocalData: num > 1025 };
    }
    if (num >= 1 && num <= 1025) {
      return { pokemonKey: String(num), numericId: num, useLocalData: false };
    }
  }
  return { pokemonKey: param, numericId: 0, useLocalData: false };
}

/** Build a Pokemon detail object from local POKEMON_DATA (for forms/megas) */
function buildLocalPokemon(key) {
  const data = POKEMON_DATA[key];
  if (!data) return null;
  const { hp, atk, def, spa, spd, spe } = data.baseStats;
  return {
    key,
    name: key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    pokedexId: data.id,
    types: data.types,
    stats: [
      { name: 'hp', base: hp },
      { name: 'attack', base: atk },
      { name: 'defense', base: def },
      { name: 'special-attack', base: spa },
      { name: 'special-defense', base: spd },
      { name: 'speed', base: spe },
    ],
    abilities: (data.abilities ?? []).map(name => ({ name, isHidden: false })),
    height: null,
    weight: data.weight ?? null,
    capture_rate: null,
    flavor_text_entries: [],
    sprites: null,
    isLocal: true,
  };
}

// Tatsugiri megas share the same artwork — use stretchy (10324) for all
const SPRITE_REMAP = { 10322: 10324, 10323: 10324 };

/** Get sprite ID — all form IDs now have artwork in the PokeAPI sprites repo */
function getDetailSpriteId(id) {
  return SPRITE_REMAP[id] ?? id;
}

// ── Component ────────────────────────────────────────────────────────────────

function PokemonDetail() {
  const { id: idParam } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { pokemonKey, numericId, useLocalData } = useMemo(() => resolveParam(idParam), [idParam]);

  const localPokemon = useMemo(
    () => (useLocalData ? buildLocalPokemon(pokemonKey) : null),
    [useLocalData, pokemonKey]
  );

  const apiPokemon = useSelector(state => state.pokemon.pokemonDetails[numericId]);
  const apiError   = useSelector(state => state.pokemon.error[numericId]);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const pokedexId     = useLocalData ? (localPokemon?.pokedexId ?? 0) : numericId;
  const isFav         = useSelector(selectIsFavorite(pokedexId));
  const isShinyTracked = useSelector(selectIsShiny(pokedexId));

  useEffect(() => {
    if (!useLocalData && numericId > 0 && !apiPokemon) {
      dispatch(fetchPokemonById(numericId));
    }
  }, [dispatch, numericId, apiPokemon, useLocalData]);

  const shouldShowShiny = useRef(Math.floor(Math.random() * 4096) === 0).current;

  useEffect(() => {
    if (shouldShowShiny && isAuthenticated && !useLocalData && apiPokemon && !isShinyTracked) {
      dispatch(addShiny({ pokedexId: apiPokemon.pokedexId, name: apiPokemon.name }));
    }
  }, [shouldShowShiny, isAuthenticated, apiPokemon, isShinyTracked, dispatch, useLocalData]);

  const pokemon = useLocalData ? localPokemon : apiPokemon;

  // Learnset lookup — try the resolved key then the API name
  const learnsetKey = useMemo(() => {
    if (pokemonKey in LEARNSETS) return pokemonKey;
    if (pokemon?.name) {
      const derived = pokemon.name.toLowerCase().replace(/\s+/g, '-');
      if (derived in LEARNSETS) return derived;
    }
    return pokemonKey;
  }, [pokemonKey, pokemon]);

  const moves = LEARNSETS[learnsetKey] ?? [];

  // Navigation (prev/next only for IDs 1–1025)
  const currentKeyIndex = SORTED_BASE_KEYS.indexOf(pokemonKey);
  const isBaseForm = currentKeyIndex !== -1;

  const handleNavigation = (direction) => {
    if (!isBaseForm) return;
    const newIdx = direction === 'prev' ? currentKeyIndex - 1 : currentKeyIndex + 1;
    if (newIdx >= 0 && newIdx < SORTED_BASE_KEYS.length) {
      navigate(`/pokemon/${SORTED_BASE_KEYS[newIdx]}`);
    }
  };

  // Sprite URLs
  const spriteId    = pokemon ? getDetailSpriteId(pokemon.pokedexId) : 1;
  const artworkUrl  = shouldShowShiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${spriteId}.png`
    : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${spriteId}.png`;
  const fallbackUrl = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${spriteId}.png`;

  // Flavor text (API only)
  const flavorText = apiPokemon?.flavor_text_entries
    ?.find(e => e.language?.name === 'en')?.flavor_text
    .replace(/[\u2190-\u2195\u25B6\u25C0]/g, '')
    .replace(/\s+/g, ' ')
    .trim() ?? '';

  return (
    <div className="pokemon-detail">
      <div className="led-red" />
      <div className="led-yellow" />
      <div className="led-green" />

      {/* ── Header ── */}
      <div className="pokemon-detail__header">
        <div className="pokemon-detail__title">
          <h1>{pokemon?.name || 'Loading…'}</h1>
          <span className="pokemon-detail__number">
            #{String(pokemon?.pokedexId || '').padStart(4, '0')}
          </span>
          {isAuthenticated && pokemon && (
            <div className="pokemon-detail__actions">
              <button
                className={`poke-action-btn${isFav ? ' is-fav' : ''}`}
                onClick={() => {
                  if (isFav) dispatch(removeFavorite(pokemon.pokedexId));
                  else dispatch(addFavorite({ pokedexId: pokemon.pokedexId, name: pokemon.name }));
                }}
                aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isFav ? '★' : '☆'} Favorite
              </button>
              {isShinyTracked && (
                <span className="shiny-encountered-badge" title="You've encountered this shiny!">
                  ✦ Shiny Found!
                </span>
              )}
            </div>
          )}
        </div>
        <div className="pokemon-detail__nav-buttons">
          <button onClick={() => navigate('/database')} className="back-to-pokedex" aria-label="Back to Pokédex" />
          <div className="nav-buttons-container">
            <button
              className="nav-button prev-button"
              onClick={() => handleNavigation('prev')}
              disabled={!isBaseForm || currentKeyIndex <= 0}
              aria-label="Previous Pokémon"
            >
              <div className="d-pad-arrow" />
            </button>
            <button
              className="nav-button next-button"
              onClick={() => handleNavigation('next')}
              disabled={!isBaseForm || currentKeyIndex >= SORTED_BASE_KEYS.length - 1}
              aria-label="Next Pokémon"
            >
              <div className="d-pad-arrow" />
            </button>
          </div>
        </div>
      </div>

      {apiError && !useLocalData && (
        <div className="error-container">Failed to load Pokémon data.</div>
      )}

      {/* ── Main layout ── */}
      <div className="pokemon-detail__content">

        {/* Left panel: sprite + types + abilities + physical */}
        <div className="pokemon-detail__panel pokemon-detail__panel--left">
          <div className="pokemon-detail__sprite-wrap">
            {pokemon && (
              <img
                src={artworkUrl}
                alt={`${pokemon.name}${shouldShowShiny ? ' (Shiny)' : ''}`}
                className="pokemon-detail__image"
                onError={(e) => { e.target.onerror = null; e.target.src = fallbackUrl; }}
              />
            )}
            {shouldShowShiny && <span className="shiny-label">✦ Shiny!</span>}
          </div>

          <div className="pokemon-detail__types">
            {pokemon?.types.map(type => <TypeBadge key={type} type={type} size="lg" />)}
          </div>

          {pokemon && (
            <>
              <div className="pokemon-detail__abilities">
                <h3>Abilities</h3>
                <div className="pokemon-detail__abilities-list">
                  {pokemon.abilities.map((ability, i) => (
                    <Link
                      key={`${ability.name}-${i}`}
                      to={`/abilities/${ability.name.replace(/\s+/g, '-')}`}
                      className="pokemon-detail__ability"
                    >
                      <span className="pokemon-detail__ability-name">
                        {ability.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                      {ability.isHidden && (
                        <span className="pokemon-detail__ability-hidden">(HA)</span>
                      )}
                      {ability.percentage != null && (
                        <span className="pokemon-detail__ability-usage">
                          {ability.percentage.toFixed(1)}%
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              {(pokemon.height != null || pokemon.weight != null || pokemon.capture_rate != null) && (
                <div className="pokemon-detail__physical">
                  {pokemon.height != null && (
                    <div className="pokemon-detail__physical-stat">
                      <span>Height</span>
                      <strong>{(pokemon.height / 10).toFixed(1)} m</strong>
                    </div>
                  )}
                  {pokemon.weight != null && (
                    <div className="pokemon-detail__physical-stat">
                      <span>Weight</span>
                      <strong>{(pokemon.weight / 10).toFixed(1)} kg</strong>
                    </div>
                  )}
                  {pokemon.capture_rate != null && (
                    <div className="pokemon-detail__physical-stat">
                      <span>Catch Rate</span>
                      <strong>{pokemon.capture_rate}</strong>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right panel: base stats + moves */}
        <div className="pokemon-detail__panel pokemon-detail__panel--right">
          {pokemon && (
            <div className="pokemon-detail__stats">
              <h3>Base Stats</h3>
              {pokemon.stats.map(stat => {
                const pct = Math.min((stat.base / 255) * 100, 100);
                return (
                  <div key={stat.name} className="pokemon-detail__stat">
                    <span className="pokemon-detail__stat-name">
                      {STAT_LABELS[stat.name] ?? stat.name}
                    </span>
                    <span className="pokemon-detail__stat-value">{stat.base}</span>
                    <div className="pokemon-detail__stat-bar">
                      <div
                        className="pokemon-detail__stat-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: STAT_COLORS[stat.name] ?? '#4dabf7',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="pokemon-detail__bst">
                BST <span className="pokemon-detail__bst-value">
                  {pokemon.stats.reduce((s, st) => s + st.base, 0)}
                </span>
              </div>
            </div>
          )}

          <div className="pokemon-detail__moves">
            <h3>
              Moves
              {moves.length > 0 && (
                <span className="pokemon-detail__moves-count"> ({moves.length} in SV)</span>
              )}
            </h3>
            {moves.length > 0 ? (
              <div className="pokemon-detail__moves-list">
                {moves.map(move => (
                  <span key={move} className="pokemon-detail__move-chip">{move}</span>
                ))}
              </div>
            ) : (
              <p className="pokemon-detail__moves-empty">Not available in Scarlet/Violet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Flavor text strip */}
      {flavorText && (
        <div className="pokemon-detail__flavor">
          <p>{flavorText}</p>
        </div>
      )}
    </div>
  );
}

export default PokemonDetail;
